import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, ParamMap, Params, Route, Router, RouterStateSnapshot, UrlSegment, UrlTree } from '@angular/router';
import { catchError, delay, from, map, Observable, of, switchMap } from 'rxjs';
import { SvcAppSettings } from '@solvace/common/settings/svc-app-settings';
import { SvcModule } from '@solvace/common/settings/enums/svc-module.enum';
import { environment } from 'projects/environments/environment';
import { removeQueryParamFromUrl } from '@solvace/common/functions/helper-functions';
import { AuthService } from '../auth.service';
import { getUserLockedRedirectForStatus } from '../auth-functions';
import { AuthStatus } from '../model/user-auth.model';
import { UserService } from '../../features/user/services/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  /**
   * Constructor
   *
   * @param _userService - injected only to guarantee it's constructed (and so
   * AuthService.getAuthStatusFn is wired, see UserService's constructor) before
   * getUserStatusAuth() is ever called below; not used directly here.
   */
  constructor(
    private _authService: AuthService,
    private _router: Router,
    private _appSettings: SvcAppSettings,
    private _userService: UserService
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Can activate
   *
   * @param route
   * @param state
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this._check(redirectUrl, route.queryParamMap, state);
  }

  /**
   * Can activate child
   *
   * @param childRoute
   * @param state
   */
  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const redirectUrl = state.url === '/sign-out' ? '/' : state.url;
    return this._check(redirectUrl, childRoute.queryParamMap, state);
  }

  /**
   * Can load
   *
   * @param route
   * @param segments
   */
  canLoad(_route: Route, _segments: UrlSegment[]): Observable<boolean> | Promise<boolean> | boolean {
    return this._check('/').pipe(map((result) => result === true));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check the authenticated status
   *
   * @param redirectURL
   * @private
   */
  private _check(redirectURL: string, snapshotParams?: ParamMap, state?: RouterStateSnapshot): Observable<boolean | UrlTree> {
    // Check the authentication status
    return this._authService.check().pipe(
      switchMap((authenticated) => {
        // If the user is not authenticated...
        if (!authenticated) {
          console.warn('[NOTIFICATION] AuthGuard._check() - usuário não autenticado', {
            timestamp: new Date().toISOString(),
            hostname: typeof location !== 'undefined' ? location.hostname : 'N/A',
            module: this._appSettings.module,
            isNotificationModule: this._appSettings.module === SvcModule.Notification
          });

          const delegatesUnauthorizedToParent = [
            SvcModule.Notification,
            SvcModule.ExternalFeatures,
            SvcModule.ActionPlan,
          ].includes(this._appSettings.module);
          if (location.hostname === 'localhost' || !delegatesUnauthorizedToParent) {
            // Redirect to the sign-in page
            const redirectAsp = snapshotParams?.get('redirectAsp');
            const redirectCore = snapshotParams?.get('redirectCore');
            redirectURL = redirectURL ? removeQueryParamFromUrl(redirectURL, 'redirectURL') : '';

            this._router.navigate(['sign-in'], {
              queryParams: !redirectAsp && !redirectCore ? (redirectURL ? { redirectURL } : null) : redirectAsp ? { redirectAsp } : { redirectCore },
            });
          } else {
            console.warn('[NOTIFICATION] AuthGuard._check() - sessão expirada, chamando função de notificação', {
              timestamp: new Date().toISOString()
            });

            alert('Your session has expired. Please login again.');
            const functionsToCall = ['angularNotificationUnauthorized', 'angularExternalFeaturesUnauthorized'];
            functionsToCall.forEach((funcName) => {
              if (window[funcName] && typeof window[funcName] === 'function') {
                window[funcName]();
              }
            });
          }

          // Prevent the access
          return of(false);
        }

        if (this._authService.getExpiredToken() && !/^\/(expired-password|sign-out)(\?(.*)|)$/g.test(state?.url)) {
          console.warn('[NOTIFICATION] AuthGuard._check() - token expirado, redirecionando para expired-password', {
            timestamp: new Date().toISOString(),
            stateUrl: state?.url
          });

          this._router.navigate(['expired-password']);
          return of(false);
        }

        // AB#70106 - being `authenticated` here only means the local token looked valid
        // (see AuthService.check()/signInUsingRefreshToken()) - it says nothing about the
        // user's current backend status. Revalidate it so a Rejected/Pending/etc. user with a
        // stale-but-unexpired token lands on the matching user-locked/* screen instead of a
        // route whose data can't fully load. getUserStatusAuth() is cheap after the first call
        // in this tab (UserService.get() caches the session once bootstrapped).
        return from(this._authService.getUserStatusAuth()).pipe(
          switchMap((status) => {
            const lockedRedirect = getUserLockedRedirectForStatus(status);
            const currentPath = state?.url?.split('?')[0];
            const lockedRedirectPath = lockedRedirect ? `/${lockedRedirect.join('/')}` : null;
            const alreadyOnLockedRedirect = lockedRedirectPath != null && currentPath === lockedRedirectPath;
            const isSignOutRoute = currentPath === '/sign-out';

            if (lockedRedirect && !alreadyOnLockedRedirect && !isSignOutRoute) {
              console.warn('[NOTIFICATION] AuthGuard._check() - status não permite acesso, redirecionando', {
                timestamp: new Date().toISOString(),
                status,
                lockedRedirect
              });
              // AB#73695 - return a UrlTree instead of imperatively calling router.navigate() +
              // returning false. The imperative form pushes an extra browser history entry outside
              // the navigation Angular is already processing; combined with NoAuthGuard's own
              // imperative redirect on the sibling '' parent, a browser back-button press could
              // ping-pong between the two guards' redirects fast enough to freeze the tab.
              if (lockedRedirect[0] === 'sign-in' && status === AuthStatus.InactiveUser) {
                return of(this._router.createUrlTree(lockedRedirect, { queryParams: { authStatus: status } }));
              }

              return of(this._router.createUrlTree(lockedRedirect));
            }

            // Allow the access
            return of(true);
          }),
          catchError((error) => {
            console.error('[NOTIFICATION] AuthGuard._check() - falha ao revalidar o status, encerrando sessão', {
              timestamp: new Date().toISOString(),
              error
            });
            this._authService.signOut(undefined, { revokeRemoteSession: false }).subscribe(() => {
              this._router.navigate(['sign-in']);
            });
            return of(false);
          })
        );
      })
    );
  }
}

